import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Cloud, Loader2, ArrowRight, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        
        // Check if user must change password (admin reset)
        // Navigate BEFORE the component re-renders due to auth state change
        if (result.must_change_password) {
          toast.info('Password reset required');
          // Use replace to prevent back navigation to login
          navigate('/change-password', { state: { forced: true }, replace: true });
          return;
        }
        
        toast.success('Welcome back!');
        navigate('/dashboard', { replace: true });
      } else {
        await register(formData.email, formData.password, formData.name);
        toast.success('Account created successfully!');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex items-center justify-center relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2B8FD8 50%, #0c4a6e 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
        <div className="relative z-10 text-center px-12 max-w-lg">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Cloud className="h-12 w-12 text-white" />
            <span className="text-4xl font-bold text-white font-['Plus_Jakarta_Sans']">CloudFactory</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-4 font-['Plus_Jakarta_Sans']">
            Integration Documentation Hub
          </h1>
          <p className="text-blue-100 text-lg leading-relaxed">
            Central platform for managing, documenting, and troubleshooting your enterprise integrations with AI-powered assistance.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-6 text-center">
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="text-2xl font-bold text-white">AI</div>
              <div className="text-blue-200 text-sm">Powered Docs</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="text-2xl font-bold text-white">24/7</div>
              <div className="text-blue-200 text-sm">Support Chat</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="text-2xl font-bold text-white">100%</div>
              <div className="text-blue-200 text-sm">Searchable</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <Cloud className="h-8 w-8 text-[#2B8FD8]" />
            <span className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CloudFactory</span>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                {isLogin ? 'Welcome back' : 'Create account'}
              </CardTitle>
              <CardDescription className="text-slate-500">
                {isLogin 
                  ? 'Enter your credentials to access your documentation' 
                  : 'Get started with CloudFactory Docs Hub'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">Full Name</Label>
                    <Input
                      id="name"
                      data-testid="register-name-input"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={!isLogin}
                      className="focus-visible:ring-[#2B8FD8]"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email-input"
                    placeholder="name@cloudafactory.co"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="focus-visible:ring-[#2B8FD8]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="login-password-input"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="focus-visible:ring-[#2B8FD8]"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#2B8FD8] hover:bg-[#2075b3] text-white"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {isLogin ? 'Sign In' : 'Create Account'}
                  {!loading && <ArrowRight className="h-4 w-4 ml-2" />}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-sm text-[#2B8FD8] hover:text-[#2075b3] font-medium"
                  data-testid="toggle-auth-mode-btn"
                >
                  {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                </button>
              </div>
            </CardContent>
          </Card>

          <p className="mt-4 text-center text-sm text-slate-500">
            By continuing, you agree to CloudFactory's Terms of Service
          </p>
          <p className="mt-3 text-center">
            <Link
              to="/userguide"
              className="inline-flex items-center gap-1.5 text-sm text-[#2B8FD8] hover:text-[#2075b3] font-medium"
            >
              <BookOpen className="h-3.5 w-3.5" />
              View User Guide
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
